import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@material-ui/core";
import AddOutlinedIcon from "@material-ui/icons/AddOutlined";
import SearchOutlinedIcon from "@material-ui/icons/SearchOutlined";
import RefreshOutlinedIcon from "@material-ui/icons/RefreshOutlined";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import PublishOutlinedIcon from "@material-ui/icons/PublishOutlined";
import FilterListIcon from "@material-ui/icons/FilterList";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PeopleOutlinedIcon from "@material-ui/icons/PeopleOutlined";
import FilterPanel, { FilterSection } from "../../../components/ui/FilterPanel";
import { showAppConfirm } from "../../../context/AlertContext";
import useStyles from "./styles";
import CrmService, { apiError, childAge, fmtDate, loadCrmSettings, relativeDue, studentRemovalWarning } from "./crmService";
import { EmptyState, PageHead, StatusDot, useCrmSettings, useToast, withExtras } from "./crmUi";
import EnquiryCard from "./EnquiryCard";
import FollowUpDialog from "./FollowUpDialog";
import EnquiryFormDrawer from "./EnquiryFormDrawer";
import ImportDialog from "./ImportDialog";
import useAddEnquiry from "./useAddEnquiry";
import { downloadEnquiriesExcel } from "./crmExcel";

const PAGE_SIZE = 25;
const NO_FILTERS = { status: [], source: [], program: [], lostReason: [], assignedTo: [], from: "", to: "" };

function MultiSelect({ label, value, options, onChange }) {
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      SelectProps={{ multiple: true, renderValue: (v) => (v.length > 2 ? `${v.length} selected` : v.join(", ")) }}
      variant="outlined"
      size="small"
      fullWidth
    >
      {options.map((o) => (
        <MenuItem key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** All enquiries: search, Filters panel, Import, Export, Add; cards on phones. */
export default function CrmEnquiries() {
  const classes = useStyles();
  const history = useHistory();
  const { settings, options, inUse, tz } = useCrmSettings();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  // The dashboard's pipeline links here with ?status=...
  const [filters, setFilters] = useState(() => {
    const status = new URLSearchParams(window.location.search).get("status");
    return status ? { ...NO_FILTERS, status: [status] } : NO_FILTERS;
  });
  const [filtersOpen, setFiltersOpen] = useState(() => filters.status.length > 0);
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [logFor, setLogFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [menu, setMenu] = useState(null);
  const [toast, toastNode] = useToast();

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(
    () => ({
      q: query || undefined,
      status: filters.status.join(",") || undefined,
      source: filters.source.join(",") || undefined,
      program: filters.program.join(",") || undefined,
      lostReason: filters.lostReason.join(",") || undefined,
      assignedTo: filters.assignedTo.join(",") || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
    }),
    [query, filters]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await CrmService.list({ ...params, page, limit: PAGE_SIZE }));
    } catch (err) {
      setError(apiError(err, "Could not load enquiries.").message);
    } finally {
      setLoading(false);
    }
  }, [params, page]);

  useEffect(() => {
    load();
  }, [load]);

  // New or edited enquiries can bring new values for the filters; re-read them.
  const reload = useCallback(() => {
    load();
    loadCrmSettings().catch(() => {});
  }, [load]);

  const add = useAddEnquiry((e, msg) => {
    toast(msg);
    reload();
  });

  const activeFilters =
    filters.status.length +
    filters.source.length +
    filters.program.length +
    filters.lostReason.length +
    filters.assignedTo.length +
    (filters.from || filters.to ? 1 : 0);
  const setFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await CrmService.exportData(params);
      if (!res.items.length) {
        toast("No enquiries match these filters.", "error");
        return;
      }
      downloadEnquiriesExcel(res, tz);
      toast(res.truncated ? "Downloaded the first 5000 enquiries." : `Downloaded ${res.items.length} enquiries.`);
    } catch (err) {
      toast(apiError(err, "Could not export.").message, "error");
    } finally {
      setExporting(false);
    }
  };

  const remove = async (e) => {
    const ok = await showAppConfirm(`Delete enquiry ${e.enquiryNo} (${e.parentName || e.childName})? Its follow-up history is deleted too.${studentRemovalWarning(e)}`);
    if (!ok) return;
    try {
      const res = await CrmService.remove(e._id);
      toast(res.message);
      reload();
    } catch (err) {
      toast(apiError(err, "Could not delete.").message, "error");
    }
  };

  const open = (e) => history.push(`/app/crm/enquiries/${e._id}`);
  // Filter choices = CRM settings + values already on enquiries (renamed or imported).
  const filterOptions = (list, used) => withExtras(list, used || []);
  const staffOptions = [
    ...filterOptions((settings?.staff || []).map((s) => s.name), inUse?.assignedTo),
    { value: "__unassigned", label: "Not assigned" },
  ];
  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className={classes.page}>
      <PageHead
        title="Enquiries"
        subtitle="Every admission enquiry, from first contact to admission."
        actions={
          <>
            {/* <Button variant="outlined" startIcon={<PublishOutlinedIcon />} onClick={() => setImportOpen(true)}>
              Import
            </Button>
            <Button variant="outlined" onClick={add.openQuick}>
              Quick add
            </Button> */}
            <Button variant="contained" color="primary" startIcon={<AddOutlinedIcon />} onClick={add.openFull}>
              Add enquiry
            </Button>
          </>
        }
      />

      <div className={classes.panelTight}>
        <div className={classes.toolbar}>
          <span className={classes.count}>
            {data.total} {data.total === 1 ? "enquiry" : "enquiries"}
          </span>
          <TextField
            className={classes.search}
            placeholder="Search name, phone or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            inputProps={{ "aria-label": "Search enquiries" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Refresh">
            <IconButton className={classes.iconBtn} aria-label="Refresh" onClick={load}>
              <RefreshOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Excel (list + follow-ups)">
            <span>
              <IconButton className={classes.iconBtn} aria-label="Download Excel" onClick={doExport} disabled={exporting}>
                <GetAppOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            className={filtersOpen ? classes.filtersOn : ""}
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
          >
            Filters
            {activeFilters > 0 && <span className={classes.badge}>{activeFilters}</span>}
          </Button>
        </div>
        {loading && <LinearProgress />}

        <div className={classes.tableWrap}>
          <div className={classes.tableCol}>
            {error && !loading ? (
              <EmptyState
                title="Could not load enquiries"
                text={error}
                action={
                  <Button variant="contained" color="primary" onClick={load}>
                    Retry
                  </Button>
                }
              />
            ) : !loading && data.items.length === 0 ? (
              <EmptyState
                icon={<PeopleOutlinedIcon style={{ fontSize: 48 }} />}
                title={query || activeFilters ? "No enquiries match" : "No enquiries yet"}
                text={query || activeFilters ? "Try another search or clear the filters." : "Add one, or import your cold-call list."}
                action={
                  query || activeFilters ? (
                    <Button variant="outlined" onClick={() => { setSearch(""); setFilters(NO_FILTERS); }}>
                      Clear search and filters
                    </Button>
                  ) : (
                    <Button variant="contained" color="primary" onClick={add.openFull}>
                      Add enquiry
                    </Button>
                  )
                }
              />
            ) : (
              <>
                <div className={`${classes.tableScroll} ${classes.desktopOnly}`}>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Parent</th>
                        <th>Child</th>
                        <th>Mobile</th>
                        <th>Program</th>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Next follow-up</th>
                        {/* <th>Assigned</th> */}
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((e) => (
                        <tr key={e._id} onClick={() => open(e)}>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {e.enquiryNo}
                            <div className={classes.muted}>{fmtDate(e.enquiryDate, tz)}</div>
                          </td>
                          <td className={classes.ellipsis}>
                            <strong style={{ fontWeight: 600 }}>{e.parentName || "-"}</strong>
                            {e.relationship && <div className={classes.muted}>{e.relationship}</div>}
                          </td>
                          <td className={classes.ellipsis}>
                            {e.childName || "-"}
                            {childAge(e) && <div className={classes.muted}>{childAge(e)}</div>}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>{e.mobile}</td>
                          <td className={classes.ellipsis}>{e.program || "-"}</td>
                          <td className={classes.ellipsis}>{e.source || "-"}</td>
                          <td>
                            <StatusDot status={e.status} />
                            {e.status === "Lost" && e.lostReason && <div className={classes.muted}>{e.lostReason}</div>}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {e.dueState ? (
                              <span
                                className={classes.due}
                                style={{ color: e.dueState === "overdue" ? "#b3261e" : e.dueState === "today" ? "#8a5600" : "#1a1a1a" }}
                              >
                                {relativeDue(e.nextFollowUpDate, tz)}
                              </span>
                            ) : (
                              "-"
                            )}
                            {e.isStale && <div className={classes.muted}>No follow-up {e.daysSinceFollowUp}d</div>}
                          </td>
                          {/* <td className={classes.ellipsis}>{e.assignedTo || "-"}</td> */}
                          <td onClick={(ev) => ev.stopPropagation()}>
                            <IconButton
                              className={classes.iconBtn}
                              aria-label={`Actions for ${e.enquiryNo}`}
                              onClick={(ev) => setMenu({ anchor: ev.currentTarget, enquiry: e })}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ul className={`${classes.cardList} ${classes.mobileOnly}`}>
                  {data.items.map((e) => (
                    <EnquiryCard key={e._id} enquiry={e} tz={tz} onOpen={() => open(e)} onLog={() => setLogFor(e)} />
                  ))}
                </ul>
                <div className={classes.pager}>
                  <span>
                    Page {page + 1} of {pages}
                  </span>
                  <Button variant="outlined" size="small" disabled={page === 0 || loading} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button variant="outlined" size="small" disabled={page + 1 >= pages || loading} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              </>
            )}
          </div>

          {filtersOpen && (
            <FilterPanel onReset={() => setFilter(NO_FILTERS)}>
              <FilterSection title="Status">
                <MultiSelect label="Status" value={filters.status} options={options?.statuses || []} onChange={(v) => setFilter({ status: v })} />
              </FilterSection>
              <FilterSection title="Source">
                <MultiSelect label="Source" value={filters.source} options={filterOptions(settings?.sources, inUse?.sources)} onChange={(v) => setFilter({ source: v })} />
              </FilterSection>
              <FilterSection title="Program">
                <MultiSelect label="Program" value={filters.program} options={filterOptions(settings?.programs, inUse?.programs)} onChange={(v) => setFilter({ program: v })} />
              </FilterSection>
              <FilterSection title="Lost reason" defaultOpen={filters.lostReason.length > 0}>
                <MultiSelect
                  label="Lost reason"
                  value={filters.lostReason}
                  options={filterOptions(settings?.lostReasons, inUse?.lostReasons)}
                  onChange={(v) => setFilter({ lostReason: v })}
                />
              </FilterSection>
              <FilterSection title="Assigned to">
                <MultiSelect label="Assigned to" value={filters.assignedTo} options={staffOptions} onChange={(v) => setFilter({ assignedTo: v })} />
              </FilterSection>
              <FilterSection title="Enquiry date">
                <div style={{ display: "grid", gap: 12 }}>
                  <TextField type="date" label="From" value={filters.from} onChange={(e) => setFilter({ from: e.target.value })} variant="outlined" size="small" InputLabelProps={{ shrink: true }} fullWidth />
                  <TextField type="date" label="To" value={filters.to} onChange={(e) => setFilter({ to: e.target.value })} variant="outlined" size="small" InputLabelProps={{ shrink: true }} fullWidth />
                </div>
              </FilterSection>
            </FilterPanel>
          )}
        </div>
      </div>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setLogFor(menu.enquiry); setMenu(null); }}>Log follow-up</MenuItem>
        <MenuItem onClick={() => { setEditFor(menu.enquiry); setMenu(null); }}>Edit</MenuItem>
        <MenuItem onClick={() => { open(menu.enquiry); setMenu(null); }}>Open</MenuItem>
        <Divider />
        <MenuItem style={{ color: "#b3261e" }} onClick={() => { const e = menu.enquiry; setMenu(null); remove(e); }}>
          Delete
        </MenuItem>
      </Menu>

      <FollowUpDialog
        open={Boolean(logFor)}
        enquiry={logFor}
        onClose={() => setLogFor(null)}
        onSaved={() => {
          setLogFor(null);
          toast("Follow-up logged.");
          reload();
        }}
      />
      <EnquiryFormDrawer
        open={Boolean(editFor)}
        enquiry={editFor}
        onClose={() => setEditFor(null)}
        onSaved={(res) => {
          setEditFor(null);
          toast(res.message || "Enquiry saved.");
          reload();
        }}
      />
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(res) => {
          toast(`${res.created} enquiries imported.`);
          reload();
        }}
      />
      {add.nodes}
      {toastNode}
    </div>
  );
}
